import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  ArrowRight,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  DollarSign,
  Package,
  Menu,
  X,
  Zap,
  FileSpreadsheet,
  Clock,
  Smartphone,
  Database,
  ArrowUpRight,
  ShieldCheck,
  Layers,
  Utensils,
  Briefcase,
  Store,
  Calendar
} from 'lucide-react';

/* ─── Sparkline SVG component ──────────────────────────── */

const Sparkline = ({ data, color = '#059669', height = 32, width = 84 }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  const areaClose = `${width},${height} 0,${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`${pts.join(' ')} ${areaClose}`}
        fill={`url(#sg-${color.replace('#','')})`}
      />
      <polyline
        points={pts.join(' ')}
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={pts[pts.length - 1].split(',')[0]} cy={pts[pts.length - 1].split(',')[1]} r="3" fill={color} />
    </svg>
  );
};

/* ─── Data de Tableros Principales ───────────────────────── */

const dashboardTabs = [
  {
    id: 'ventas',
    label: 'Ventas & Facturación',
    icon: TrendingUp,
    badge: 'Comercial',
    title: 'Performance de Ventas, Márgenes y Canales',
    subtitle: 'Seguimiento en tiempo real de facturación vs. objetivos, rentabilidad por canal y ranking de clientes.',
    kpis: [
      {
        label: 'Facturación Mensual',
        value: '$24.850.000',
        subtext: 'vs. meta $23.000.000',
        delta: '+18.4%',
        targetPct: '108%',
        up: true,
        status: 'Meta superada',
        sparkline: [16.2, 17.5, 16.9, 19.4, 21.0, 22.8, 24.85],
      },
      {
        label: 'Margen Bruto Real',
        value: '38.6%',
        subtext: 'vs. 35.0% presupuestado',
        delta: '+3.6 pp',
        targetPct: '110%',
        up: true,
        status: 'Rentabilidad alta',
        sparkline: [34.1, 34.8, 35.2, 36.0, 37.1, 38.0, 38.6],
      },
      {
        label: 'Ticket Promedio',
        value: '$142.500',
        subtext: 'vs. $134.000 mes anterior',
        delta: '+6.2%',
        targetPct: '106%',
        up: true,
        status: 'En crecimiento',
        sparkline: [128, 131, 133, 136, 138, 140, 142.5],
      },
      {
        label: 'Tasa de Conversión B2B',
        value: '74.2%',
        subtext: '31 de 42 cotizaciones',
        delta: '+5.1%',
        targetPct: '98%',
        up: true,
        status: 'Pipeline activo',
        sparkline: [62, 65, 68, 70, 71, 73, 74.2],
      },
    ],
    // Gráfico de Ventas vs Presupuesto por Sucursal / Mes
    monthlyComparison: [
      { month: 'Ene', real: 17.2, target: 16.0 },
      { month: 'Feb', real: 18.5, target: 17.0 },
      { month: 'Mar', real: 16.8, target: 18.0 },
      { month: 'Abr', real: 21.0, target: 19.0 },
      { month: 'May', real: 22.8, target: 21.0 },
      { month: 'Jun', real: 24.85, target: 23.0 },
    ],
    // Canales de Venta
    channels: [
      { name: 'B2B Mayorista / Directo', amount: '$10.437.000', share: 42, margin: '41.2%', color: '#059669' },
      { name: 'Canal Digital / Tienda Online', amount: '$6.958.000', share: 28, margin: '39.5%', color: '#10b981' },
      { name: 'Distribuidores Regionales', amount: '$4.473.000', share: 18, margin: '32.0%', color: '#34d399' },
      { name: 'Ventas de Salón / Mostrador', amount: '$2.982.000', share: 12, margin: '44.0%', color: '#6ee7b7' },
    ],
    // Top Vendedores / Sucursales
    topPerformers: [
      { name: 'Sucursal Centro (Martín G.)', sales: '$9.840.000', quota: '118%', status: 'Superado' },
      { name: 'Canal Corporativo (Lucía V.)', sales: '$8.210.000', quota: '105%', status: 'Cumplido' },
      { name: 'Sucursal Norte (Andrés R.)', sales: '$6.800.000', quota: '98%', status: 'En meta' },
    ],
    items: [
      'Ventas vs presupuesto comparativo en tiempo real por sucursal y vendedor',
      'Cálculo automático de margen neto descontando comisiones e impuestos',
      'Proyección de cierre de mes al día con algoritmos predictivos',
      'Ranking de clientes por volumen de compra y margen de contribución',
    ],
  },
  {
    id: 'finanzas',
    label: 'Flujo de Fondos & Cobranzas',
    icon: DollarSign,
    badge: 'Finanzas',
    title: 'Cashflow Proyectado, Liquidez y Cuentas a Cobrar',
    subtitle: 'Control estricto de caja, antigüedad de deuda de clientes y punto de equilibrio operativo al día.',
    kpis: [
      {
        label: 'Liquidez Disponible (Caja)',
        value: '$18.200.000',
        subtext: '45 días de cobertura operativa',
        delta: '+12.5%',
        targetPct: 'Óptimo',
        up: true,
        status: '45 días cobertura',
        sparkline: [12.0, 13.5, 14.8, 15.2, 16.4, 17.5, 18.2],
      },
      {
        label: 'Cuentas por Cobrar Total',
        value: '$9.450.000',
        subtext: '78% dentro de término',
        delta: '-8.4%',
        targetPct: 'Saludable',
        up: true,
        status: '78% al día',
        sparkline: [11.8, 11.2, 10.5, 10.1, 9.9, 9.7, 9.45],
      },
      {
        label: 'Plazo Medio de Cobro (DSO)',
        value: '21 días',
        subtext: 'Reducción de 9 días vs histórico',
        delta: '-9 días',
        targetPct: 'Mejorado',
        up: true,
        status: 'Excelente',
        sparkline: [32, 29, 27, 25, 24, 22, 21],
      },
      {
        label: 'Punto de Equilibrio Mes',
        value: '$12.300.000',
        subtext: 'Superado el día 14 del mes',
        delta: 'Día 14',
        targetPct: '100%',
        up: true,
        status: 'Superado',
        sparkline: [4.2, 7.8, 10.5, 12.3, 15.8, 20.1, 24.8],
      },
    ],
    // Cashflow proyectado a 3 meses
    cashflowBars: [
      { period: 'Sem 1', inFlow: 6.8, outFlow: 4.2 },
      { period: 'Sem 2', inFlow: 7.4, outFlow: 5.1 },
      { period: 'Sem 3', inFlow: 8.9, outFlow: 4.8 },
      { period: 'Sem 4', inFlow: 9.5, outFlow: 6.2 },
    ],
    // Aging de Deuda
    agingRanges: [
      { label: '0 a 30 días (Al día)', amount: '$7.371.000', share: 78, risk: 'Bajo', color: '#059669' },
      { label: '31 a 60 días (Vencido leve)', amount: '$1.417.500', share: 15, risk: 'Medio', color: '#f59e0b' },
      { label: '+60 días (En gestión)', amount: '$661.500', share: 7, risk: 'Alerta', color: '#ef4444' },
    ],
    // Estructura de Costos
    costBreakdown: [
      { concept: 'Costos de Mercadería / Insumos', pct: '52%', amount: '$9.460.000' },
      { concept: 'Sueldos & Cargas Sociales', pct: '24%', amount: '$4.368.000' },
      { concept: 'Gastos Operativos & Alquileres', pct: '14%', amount: '$2.548.000' },
      { concept: 'Impuestos & Servicios', pct: '10%', amount: '$1.820.000' },
    ],
    items: [
      'Flujo de caja proyectado a 30, 60 y 90 días con integración bancaria',
      'Antigüedad de deuda con alertas automáticas de facturas por vencer',
      'Separación de costos fijos vs. variables para cálculo de margen de seguridad',
      'EBITDA, margen operativo y rentabilidad neta en moneda local y dólares',
    ],
  },
  {
    id: 'stock',
    label: 'Stock & Gestión de Compras',
    icon: Package,
    badge: 'Operaciones',
    title: 'Inventario, Rotación ABC y Puntos de Reorden',
    subtitle: 'Evitá quiebres de stock y capital inmovilizado con sincronización automática entre almacén y ventas.',
    kpis: [
      {
        label: 'Valor Total en Inventario',
        value: '$42.100.000',
        subtext: 'Valorizado a costo de reposición',
        delta: 'Costo act.',
        targetPct: 'Balanceado',
        up: true,
        status: 'Valuado al día',
        sparkline: [38.5, 39.2, 40.1, 40.8, 41.5, 41.9, 42.1],
      },
      {
        label: 'Días de Cobertura Promedio',
        value: '32 días',
        subtext: 'Rango objetivo: 25 a 35 días',
        delta: 'Nivel óptimo',
        targetPct: 'Óptimo',
        up: true,
        status: 'Sin sobrestock',
        sparkline: [26, 28, 30, 31, 29, 33, 32],
      },
      {
        label: 'SKUs en Punto Crítico',
        value: '4 productos',
        subtext: 'Menos de 7 días de stock',
        delta: 'Acción req.',
        targetPct: 'Atención',
        up: false,
        status: 'Alerta de orden',
        sparkline: [1, 2, 1, 3, 2, 3, 4],
      },
      {
        label: 'Capital Inmovilizado (>90d)',
        value: '3.8%',
        subtext: 'Reducción del 8.4% este trimestre',
        delta: '-4.6 pp',
        targetPct: 'Mínimo',
        up: true,
        status: 'Excelente rotación',
        sparkline: [10.2, 9.1, 7.8, 6.4, 5.2, 4.3, 3.8],
      },
    ],
    // Rotación ABC
    abcClasses: [
      { category: 'Clase A (Alta rotación, 75% ventas)', skus: '48 SKUs', value: '$28.400.000', color: '#059669', share: 67 },
      { category: 'Clase B (Rotación media, 20% ventas)', skus: '112 SKUs', value: '$10.200.000', color: '#10b981', share: 24 },
      { category: 'Clase C (Baja rotación / stock seguridad)', skus: '240 SKUs', value: '$3.500.000', color: '#6ee7b7', share: 9 },
    ],
    // Alertas de Reorden
    reorderAlerts: [
      { sku: 'Válvula Reguladora Industrial 2"', current: '12 un.', min: '25 un.', supplier: 'MetalSur S.A.', leadTime: '4 días' },
      { sku: 'Cable Siliconado Reforzado 100m', current: '3 rollos', min: '10 rollos', supplier: 'ElectroNorte', leadTime: '2 días' },
      { sku: 'Sensor de Presión Digital Pro', current: '5 un.', min: '15 un.', supplier: 'SensoTech', leadTime: '6 días' },
    ],
    items: [
      'Alertas automáticas antes del quiebre de stock enviadas a compras',
      'Clasificación ABC automática por rotación, margen y volumen',
      'Identificación temprana de mercadería estancada con sugerencia de liquidación',
      'Sincronización multi-depósito con e-commerce y sistemas de facturación',
    ],
  },
];

/* ─── Data de Plantillas por Industria ───────────────────── */

const industries = [
  {
    id: 'gastronomia',
    label: 'Gastronomía & Cadenas',
    icon: Utensils,
    sub: 'Restaurantes, Bares & Franquicias',
    description: 'Control de Food Cost diario, rotación de mesas, ingeniería de menú y rentabilidad por salón vs. delivery.',
    kpis: [
      { label: 'Food Cost % Real', value: '28.4%', target: 'Meta < 30%', delta: '-1.8% vs ant.', up: true, tag: 'En objetivo' },
      { label: 'RevPASH (Venta x Asiento/Hora)', value: '$4.850', target: 'Salón Principal', delta: '+12.4%', up: true, tag: 'Hora pico' },
      { label: 'Ticket Promedio Salón', value: '$18.200', target: 'Promedio 2.4 personas', delta: '+6.5%', up: true, tag: 'Cena' },
      { label: 'Margen Canal Delivery', value: '34.2%', target: 'Neto comisiones de apps', delta: '+4.1%', up: true, tag: 'Rentable' },
    ],
    chartLabel: 'Facturación por Día de la Semana (en Millones $)',
    bars: [
      { day: 'Lun', val: 1.4, max: 4.8 },
      { day: 'Mar', val: 1.9, max: 4.8 },
      { day: 'Mié', val: 2.3, max: 4.8 },
      { day: 'Jue', val: 3.1, max: 4.8 },
      { day: 'Vie', val: 4.6, max: 4.8 },
      { day: 'Sáb', val: 4.8, max: 4.8, highlight: true },
      { day: 'Dom', val: 3.7, max: 4.8 },
    ],
    menuMatrix: [
      { name: 'Ojo de Bife con Papas Rústicas', type: 'Estrella (Alto Margen / Alta Rotación)', share: '36%', margin: '68%' },
      { name: 'Risotto de Hongos Silvestres', type: 'Estrella (Alto Margen / Alta Rotación)', share: '24%', margin: '72%' },
      { name: 'Hamburguesa Smash Doble Cheddar', type: 'Caballo de Batalla (Alta Rotación)', share: '22%', margin: '54%' },
      { name: 'Salmón Grillado a las Hierbas', type: 'Oportunidad (Alto Margen / Rot. Media)', share: '18%', margin: '64%' },
    ],
  },
  {
    id: 'estudio',
    label: 'Estudios Profesionales',
    icon: Briefcase,
    sub: 'Contadores, Abogados & Consultoras',
    description: 'Rentabilidad por cliente/abono, horas facturables vs. operativas y tasa de recupero de honorarios.',
    kpis: [
      { label: 'Honorarios Mensuales', value: '$18.450.000', target: 'Abonos recurrentes 82%', delta: '+14.6%', up: true, tag: 'Facturado' },
      { label: 'Utilización Horas Facturables', value: '86.4%', target: 'Meta del equipo: 80%', delta: '+6.4 pp', up: true, tag: 'Alta eficiencia' },
      { label: 'Rentabilidad Promedio x Cliente', value: '58.2%', target: 'Margen neto operativo', delta: '+3.1%', up: true, tag: 'Abonos A' },
      { label: 'Días Promedio de Cobranza', value: '16 días', target: 'Débito automático 64%', delta: '-6 días', up: true, tag: 'Baja morosidad' },
    ],
    chartLabel: 'Evolución de Honorarios Facturados por Mes (en Millones $)',
    bars: [
      { day: 'Ene', val: 12.8, max: 18.45 },
      { day: 'Feb', val: 13.9, max: 18.45 },
      { day: 'Mar', val: 14.5, max: 18.45 },
      { day: 'Abr', val: 16.2, max: 18.45 },
      { day: 'May', val: 17.1, max: 18.45 },
      { day: 'Jun', val: 18.45, max: 18.45, highlight: true },
    ],
    menuMatrix: [
      { name: 'Abonos Contables & Impositivos Pymes', type: 'Recurrente Mensual (48 Clientes)', share: '46%', margin: '62%' },
      { name: 'Asesoría en Reestructuración Societaria', type: 'Servicio por Proyecto / Éxito', share: '28%', margin: '74%' },
      { name: 'Liquidación de Sueldos & Cargas Masivas', type: 'Recurrente Mensual (32 Clientes)', share: '16%', margin: '52%' },
      { name: 'Auditorías de Balance Anual', type: 'Estacional (Q1 / Q2)', share: '10%', margin: '68%' },
    ],
  },
  {
    id: 'retail',
    label: 'Retail & Distribución',
    icon: Store,
    sub: 'Comercios Mayoristas & E-commerce',
    description: 'Ventas omnicanal, margen bruto por familia de producto, rotación de stock y rendimiento por metro cuadrado.',
    kpis: [
      { label: 'Venta Omnicanal Mes', value: '$52.300.000', target: 'Tienda física + Tiendanube', delta: '+21.3%', up: true, tag: 'Crecimiento' },
      { label: 'GMROI (Retorno s/ Inversión Stock)', value: '3.4x', target: 'Meta: > 2.8x', delta: '+0.5x', up: true, tag: 'Excelente' },
      { label: 'Participación E-commerce', value: '44.8%', target: 'vs. 36% año anterior', delta: '+8.8 pp', up: true, tag: 'Digital' },
      { label: 'Tasa de Devolución / Fallas', value: '1.2%', target: 'Umbral máximo: 2.5%', delta: '-0.6%', up: true, tag: 'Controlado' },
    ],
    chartLabel: 'Ventas Semanales por Canal ($ Millones)',
    bars: [
      { day: 'Sem 1', val: 11.2, max: 15.8 },
      { day: 'Sem 2', val: 12.6, max: 15.8 },
      { day: 'Sem 3', val: 13.9, max: 15.8 },
      { day: 'Sem 4', val: 15.8, max: 15.8, highlight: true },
    ],
    menuMatrix: [
      { name: 'Línea Electrodomésticos & Hogar', type: 'Mayor Volumen de Facturación', share: '44%', margin: '32%' },
      { name: 'Accesorios & Conectividad Pro', type: 'Mayor Margen Unitario', share: '26%', margin: '58%' },
      { name: 'Herramientas & Bazar Industrial', type: 'Rotación Media y Alta Fidelidad', share: '18%', margin: '42%' },
      { name: 'Repuestos & Insumos Oficiales', type: 'Ventas de Post-venta Recurrente', share: '12%', margin: '48%' },
    ],
  },
];

const faqs = [
  { q: '¿Qué fuentes de datos se pueden conectar?', a: 'Excel, Google Sheets, bases de datos SQL, ERPs locales y en la nube (Tango, SAP, Bejerman, Holded, Finnegans), plataformas de e-commerce (Tiendanube, MercadoLibre, Shopify, WooCommerce) y cualquier sistema con API.' },
  { q: '¿Cuánto demora la implementación?', a: 'Un tablero estándar queda en producción entre 2 y 3 semanas desde el relevamiento inicial, con validación de datos paso a paso.' },
  { q: '¿Cómo accede el equipo a los reportes?', a: 'Disponibles 24/7 en la nube segura de Microsoft Power BI, con acceso restringido por roles y permisos desde PC, tablet o app oficial en el celular.' },
  { q: '¿Cuánto cuesta la licencia de Power BI?', a: 'Las licencias Power BI Pro oficiales de Microsoft cuestan ~$10 USD/usuario/mes. Diseñamos la arquitectura de visualización para minimizar la cantidad de licencias pagas necesarias.' },
];

const proofStats = [
  { value: '2–3 sem.', label: 'Implementación', icon: Clock },
  { value: 'Automática', label: 'Actualización', icon: Zap },
  { value: 'PC & móvil', label: 'Acceso 24/7', icon: Smartphone },
  { value: '+15', label: 'Fuentes de datos', icon: Database },
];

/* ─── Componente Principal ───────────────────────────────── */

const PowerBIServicePage = () => {
  const [activeTab, setActiveTab] = useState('ventas');
  const [activeIndustry, setActiveIndustry] = useState('gastronomia');
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const currentTab = dashboardTabs.find(t => t.id === activeTab) || dashboardTabs[0];
  const currentIndustry = industries.find(i => i.id === activeIndustry) || industries[0];

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <Helmet>
        <title>Tableros Power BI para Pymes | NeuraSur Business Intelligence</title>
        <meta name="description" content="Consultoría especializada en Microsoft Power BI para pymes en Argentina. Tableros interactivos automáticos de ventas, finanzas y stock conectados a tus sistemas." />
        <link rel="canonical" href="https://www.neurasur.com/servicios/power-bi-business-intelligence" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Service",
          "name": "Tableros Power BI para Pymes",
          "provider": { "@type": "Organization", "name": "NeuraSur", "url": "https://www.neurasur.com" },
          "areaServed": "Argentina", "serviceType": "Business Intelligence & Data Analytics"
        })}</script>
      </Helmet>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-slate-50 border border-slate-200 p-1.5 rounded-lg">
              <img src="/logo.webp" onError={e => { e.target.src = '/logo.png'; }} alt="NeuraSur" className="h-7 w-auto" />
            </div>
            <span className="font-bold text-slate-900 text-[15px] tracking-tight">NeuraSur</span>
          </Link>

          {/* Navegación limpia: Solo Servicios y Blog */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/servicios/power-bi-business-intelligence"
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-emerald-700 bg-emerald-50/80 transition-all"
            >
              Servicios
            </Link>
            <Link
              to="/blog"
              className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all"
            >
              Blog
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/5491171299730?text=Hola!%20Quiero%20una%20demo%20de%20Power%20BI."
              target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm"
            >
              Hablar con un experto <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-700"
              aria-label="Menú"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden border-t border-slate-200 bg-white shadow-xl"
            >
              <div className="px-5 py-4 space-y-1 bg-white">
                <Link to="/servicios/power-bi-business-intelligence" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Servicios
                </Link>
                <Link to="/blog" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Blog
                </Link>
                <a
                  href="https://wa.me/5491171299730?text=Hola!%20Quiero%20una%20demo%20de%20Power%20BI."
                  target="_blank" rel="noopener noreferrer"
                  className="block text-center mt-3 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                >
                  Hablar con un experto
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-28 pb-16 px-5 sm:px-8">
        <div className="max-w-[1200px] mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-8">
            <Link to="/" className="hover:text-slate-600 transition-colors">Inicio</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600 font-medium">Power BI para Pymes</span>
          </div>

          <div className="grid lg:grid-cols-[1fr_420px] gap-12 items-center">
            {/* Left — Copy */}
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full mb-5 tracking-wide uppercase">
                <BarChart3 className="w-3.5 h-3.5 text-emerald-600" /> Business Intelligence & Analytics
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-[54px] font-extrabold text-slate-900 leading-[1.12] tracking-tight">
                Tus datos, claros<br />y en tiempo real
              </h1>

              <p className="mt-5 text-base sm:text-[17px] text-slate-600 leading-relaxed max-w-lg">
                Transformamos tus planillas y ERPs en tableros de control automáticos con <strong className="text-slate-900 font-semibold">Microsoft Power BI</strong>. Decisiones estratégicas basadas en números reales, sin esperar al cierre mensual.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/5491171299730?text=Hola!%20Me%20interesa%20implementar%20tableros%20Power%20BI."
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-6 py-3.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  <MessageSquare className="w-4 h-4" /> Consultar sin cargo
                </a>
                <a
                  href="#tableros"
                  className="inline-flex items-center justify-center gap-1.5 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 px-6 py-3.5 rounded-lg text-sm font-medium transition-all"
                >
                  Ver tableros disponibles <ChevronDown className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right — Proof Panel */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between bg-white">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Capacidades del servicio</span>
                <span className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  Producción activa
                </span>
              </div>

              <div className="grid grid-cols-2 divide-x divide-y divide-slate-200">
                {proofStats.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="p-5 bg-white hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold">{s.label}</p>
                        <Icon className="w-4 h-4 text-emerald-600" />
                      </div>
                      <p className="text-xl font-extrabold text-slate-900 tracking-tight">{s.value}</p>
                    </div>
                  );
                })}
              </div>

              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
                <span className="text-[11px] text-slate-500 font-medium">Tecnología oficial:</span>
                <span className="text-[11px] font-bold text-slate-800">Microsoft Power BI Pro</span>
                <span className="ml-auto text-[11px] text-emerald-700 font-semibold bg-emerald-100/60 px-2 py-0.5 rounded">Cloud 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECCIÓN 1: Tableros Principales por Área (Interactive Power BI Canvas) ── */}
      <section id="tableros" className="py-16 px-5 sm:px-8 bg-slate-50/70 border-y border-slate-200">
        <div className="max-w-[1200px] mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
                <Layers className="w-3 h-3" /> Módulos Disponibles
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Tableros de Gestión para Pymes</h2>
              <p className="mt-1 text-sm text-slate-500">Hacé clic en cada pestaña para explorar los datos reales que verás en tu empresa.</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shrink-0">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span>Actualizado automáticamente: <strong>Hoy, 08:00 hs</strong></span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 border-b border-slate-200 mb-8 overflow-x-auto pb-1">
            {dashboardTabs.map(t => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`relative flex items-center gap-2 px-5 py-3 rounded-t-xl text-sm font-semibold whitespace-nowrap transition-all shrink-0 ${
                    active
                      ? 'bg-white text-slate-900 border-t border-x border-slate-200 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/60'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {t.label}
                  {active && (
                    <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                      En vivo
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Canvas Interactivo del Dashboard */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
            >
              {/* Chrome Header del Canvas (Power BI Embedded Style) */}
              <div className="px-6 py-4 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                  </div>
                  <span className="text-slate-500 font-mono">|</span>
                  <span className="text-xs font-semibold text-slate-200 tracking-wide">{currentTab.title}</span>
                  <span className="hidden sm:inline-flex text-[10px] font-bold text-emerald-400 bg-emerald-950/80 border border-emerald-500/30 px-2 py-0.5 rounded uppercase">
                    Power BI Pro Live
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 hidden sm:inline">Filtro: <strong>Mes Actual (Junio 2026)</strong></span>
                  <a
                    href={`https://wa.me/5491171299730?text=${encodeURIComponent(`Hola! Me interesa implementar el tablero de ${currentTab.label} en mi empresa.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-950 bg-emerald-400 hover:bg-emerald-300 px-3.5 py-1.5 rounded-lg transition-colors"
                  >
                    Solicitar este tablero <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>

              <div className="p-6">
                {/* 4 KPIs Ejecutivos con Sparklines */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {currentTab.kpis.map((kpi, idx) => (
                    <div key={idx} className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{kpi.label}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${kpi.up ? 'text-emerald-700 bg-emerald-100/70' : 'text-amber-700 bg-amber-100/70'}`}>
                            {kpi.status}
                          </span>
                        </div>
                        <p className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none my-1">{kpi.value}</p>
                        <p className="text-xs text-slate-400 mb-2">{kpi.subtext}</p>
                      </div>

                      <div className="flex items-end justify-between pt-2 border-t border-slate-200/60 mt-2">
                        <div className="flex items-center gap-1">
                          {kpi.up ? (
                            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          ) : (
                            <TrendingDown className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          )}
                          <span className={`text-xs font-bold ${kpi.up ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {kpi.delta}
                          </span>
                        </div>
                        <Sparkline data={kpi.sparkline} color={kpi.up ? '#059669' : '#d97706'} width={84} height={30} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Área de Gráficos Analíticos Integrados */}
                <div className="grid lg:grid-cols-12 gap-6 mb-6">
                  {/* Gráfico Principal Izquierdo (7 Cols) */}
                  <div className="lg:col-span-7 bg-slate-50/60 border border-slate-200/80 rounded-xl p-5">
                    {currentTab.id === 'ventas' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">Evolución de Ventas vs. Presupuesto 2026</h4>
                            <p className="text-xs text-slate-500">Comparativa mensual real facturado vs meta presupuestada ($M)</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-medium">
                            <span className="flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-600" /> Real</span>
                            <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300" /> Meta</span>
                          </div>
                        </div>

                        <div className="space-y-3 pt-2">
                          {currentTab.monthlyComparison.map((m, i) => {
                            const maxVal = 26;
                            return (
                              <div key={i} className="flex items-center gap-3">
                                <span className="text-xs font-bold text-slate-500 w-8">{m.month}</span>
                                <div className="flex-1 space-y-1">
                                  <div className="h-2.5 bg-slate-200/80 rounded-full overflow-hidden flex">
                                    <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${(m.real / maxVal) * 100}%` }} />
                                  </div>
                                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
                                    <div className="h-full bg-slate-400/60 rounded-full transition-all" style={{ width: `${(m.target / maxVal) * 100}%` }} />
                                  </div>
                                </div>
                                <span className="text-xs font-bold text-slate-900 w-16 text-right">${m.real}M</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {currentTab.id === 'finanzas' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">Cash Flow Semanal Proyectado</h4>
                            <p className="text-xs text-slate-500">Ingresos proyectados por cobranzas vs Egresos comprometidos ($M)</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-medium">
                            <span className="flex items-center gap-1.5 text-emerald-700"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Cobranzas</span>
                            <span className="flex items-center gap-1.5 text-amber-700"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Pagos/Gastos</span>
                          </div>
                        </div>

                        <div className="space-y-4 pt-2">
                          {currentTab.cashflowBars.map((b, i) => (
                            <div key={i} className="bg-white border border-slate-200/60 rounded-lg p-3">
                              <div className="flex justify-between text-xs font-bold text-slate-700 mb-1.5">
                                <span>{b.period}</span>
                                <span className="text-emerald-700">Saldo Neto: +${(b.inFlow - b.outFlow).toFixed(1)}M</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <span className="text-[10px] text-slate-400 block mb-0.5">Ingreso: ${b.inFlow}M</span>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(b.inFlow / 10) * 100}%` }} />
                                  </div>
                                </div>
                                <div>
                                  <span className="text-[10px] text-slate-400 block mb-0.5">Egreso: ${b.outFlow}M</span>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(b.outFlow / 10) * 100}%` }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentTab.id === 'stock' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">Matriz de Rotación ABC de Inventario</h4>
                            <p className="text-xs text-slate-500">Distribución de capital inmovilizado según volumen y rentabilidad</p>
                          </div>
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded">
                            400 SKUs totales
                          </span>
                        </div>

                        <div className="space-y-3.5 pt-2">
                          {currentTab.abcClasses.map((abc, i) => (
                            <div key={i} className="bg-white border border-slate-200/60 rounded-xl p-3.5">
                              <div className="flex justify-between items-center text-xs font-bold text-slate-800 mb-1">
                                <span>{abc.category}</span>
                                <span className="text-slate-900">{abc.value} ({abc.share}%)</span>
                              </div>
                              <p className="text-[11px] text-slate-400 mb-2">Comprende {abc.skus}</p>
                              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${abc.share}%`, background: abc.color }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Panel Secundario Derecho (5 Cols) */}
                  <div className="lg:col-span-5 bg-slate-50/60 border border-slate-200/80 rounded-xl p-5">
                    {currentTab.id === 'ventas' && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-1">Mix de Facturación por Canal</h4>
                        <p className="text-xs text-slate-500 mb-4">Volumen y margen promedio de cada unidad</p>
                        <div className="space-y-3">
                          {currentTab.channels.map((ch, i) => (
                            <div key={i} className="bg-white border border-slate-200/60 rounded-lg p-3">
                              <div className="flex justify-between items-center text-xs font-bold mb-1">
                                <span className="text-slate-800 truncate max-w-[150px]">{ch.name}</span>
                                <span className="text-slate-900">{ch.amount}</span>
                              </div>
                              <div className="flex justify-between text-[11px] text-slate-500 mb-1.5">
                                <span>Participación: {ch.share}%</span>
                                <span className="text-emerald-700 font-semibold">Margen: {ch.margin}</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${ch.share}%`, background: ch.color }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentTab.id === 'finanzas' && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-1">Aging de Cuentas por Cobrar</h4>
                        <p className="text-xs text-slate-500 mb-4">Antigüedad de facturas impagas y nivel de riesgo</p>
                        <div className="space-y-3">
                          {currentTab.agingRanges.map((ag, i) => (
                            <div key={i} className="bg-white border border-slate-200/60 rounded-lg p-3">
                              <div className="flex justify-between items-center text-xs font-bold mb-1">
                                <span className="text-slate-800">{ag.label}</span>
                                <span className="text-slate-900">{ag.amount}</span>
                              </div>
                              <div className="flex justify-between text-[11px] text-slate-500 mb-1.5">
                                <span>{ag.share}% del total pendiente</span>
                                <span className="font-semibold" style={{ color: ag.color }}>Riesgo {ag.risk}</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${ag.share}%`, background: ag.color }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentTab.id === 'stock' && (
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-1">Alertas Tempranas de Reorden</h4>
                        <p className="text-xs text-slate-500 mb-4">SKUs críticos que requieren emisión de orden de compra</p>
                        <div className="space-y-2.5">
                          {currentTab.reorderAlerts.map((al, i) => (
                            <div key={i} className="bg-white border border-slate-200/60 rounded-lg p-3 text-xs">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="font-bold text-slate-900 leading-snug">{al.sku}</span>
                                <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">Reorden</span>
                              </div>
                              <div className="flex justify-between text-[11px] text-slate-500 mt-1">
                                <span>Stock actual: <strong className="text-red-600">{al.current}</strong> (Mín. {al.min})</span>
                                <span>Lead time: {al.leadTime}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1">Proveedor: {al.supplier}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features y Beneficios del Tablero */}
                <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-5">
                  <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Qué incluye este tablero en tu empresa:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {currentTab.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── SECCIÓN 2: Plantillas Especializadas por Industria ── */}
      <section className="py-16 px-5 sm:px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              <Store className="w-3.5 h-3.5 text-slate-600" /> Plantillas Sectoriales
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Especialización por Industria</h2>
            <p className="mt-1 text-sm text-slate-500 max-w-2xl">
              Cada negocio tiene sus propios indicadores clave. Diseñamos tableros a medida según las particularidades de tu rubro.
            </p>
          </div>

          {/* Selector de Industria */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
            {industries.map(ind => {
              const Icon = ind.icon;
              const active = activeIndustry === ind.id;
              return (
                <button
                  key={ind.id}
                  onClick={() => setActiveIndustry(ind.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap border transition-all shrink-0 ${
                    active
                      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-emerald-400' : 'text-slate-400'}`} />
                  {ind.label}
                </button>
              );
            })}
          </div>

          {/* Dashboard Template de Industria */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndustry.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
            >
              {/* Chrome bar */}
              <div className="px-6 py-3.5 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-3 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                  </div>
                  <span className="text-xs font-semibold text-slate-200">Plantilla Especializada: {currentIndustry.sub}</span>
                </div>
                <span className="text-xs text-emerald-400 font-medium flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Configuración Lista para Despliegue
                </span>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <p className="text-sm text-slate-600 font-medium">{currentIndustry.description}</p>
                </div>

                {/* 4 KPIs de la industria */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {currentIndustry.kpis.map((k, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200/80 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100/70 px-1.5 py-0.5 rounded">{k.tag}</span>
                      </div>
                      <p className="text-xl font-extrabold text-slate-900 my-1">{k.value}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{k.target}</span>
                        <span className="text-emerald-600 font-bold">{k.delta}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Gráficos del template */}
                <div className="grid lg:grid-cols-12 gap-6">
                  {/* Gráfico de barras */}
                  <div className="lg:col-span-7 bg-slate-50/60 border border-slate-200/80 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4">{currentIndustry.chartLabel}</h4>
                    <div className="flex items-end gap-2.5 h-32 pt-4">
                      {currentIndustry.bars.map((b, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                          <span className="text-[10px] font-bold text-slate-600">${b.val}</span>
                          <div
                            className="w-full rounded-t-md transition-all"
                            style={{
                              height: `${(b.val / b.max) * 90}px`,
                              background: b.highlight ? '#059669' : '#cbd5e1',
                            }}
                          />
                          <span className="text-[10px] text-slate-500 font-semibold">{b.day}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Matriz o desglose */}
                  <div className="lg:col-span-5 bg-slate-50/60 border border-slate-200/80 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">Ranking de Líneas y Rentabilidad</h4>
                    <div className="space-y-2.5">
                      {currentIndustry.menuMatrix.map((m, i) => (
                        <div key={i} className="bg-white border border-slate-200/60 rounded-lg p-2.5 text-xs">
                          <div className="flex justify-between font-bold text-slate-900 mb-0.5">
                            <span className="truncate max-w-[170px]">{m.name}</span>
                            <span className="text-emerald-700">{m.margin} mg.</span>
                          </div>
                          <div className="flex justify-between text-[11px] text-slate-400">
                            <span>{m.type}</span>
                            <span>{m.share} mix</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <p className="mt-4 text-xs text-slate-400 text-center">
            * Datos ilustrativos representativos. Durante la consultoría nos conectamos directamente a tus fuentes de información.
          </p>
        </div>
      </section>

      {/* ── Comparativa Excel vs. Power BI ── */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50/50 border-t border-slate-200">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-2">¿Por qué dar el salto de Excel a Power BI?</h2>
          <p className="text-sm text-slate-500 mb-10 max-w-2xl">Excel es excelente para análisis ad-hoc, pero no fue diseñado para ser el tablero de control de tu empresa.</p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Excel */}
            <div className="border border-slate-200 bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-base">Planillas Manuales</p>
                  <p className="text-xs text-slate-400">Excel / Google Sheets descentralizado</p>
                </div>
              </div>
              <ul className="space-y-3.5">
                {[
                  '3 a 5 días de trabajo administrativo para armar el informe mensual.',
                  'Múltiples versiones del mismo archivo circulando por correo (riesgo de datos erróneos).',
                  'Fórmulas rotas y planillas pesadas que se cuelgan al abrir.',
                  'Imposibilidad de consultar números actualizados de forma segura desde el celular.',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-500">
                    <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Power BI */}
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-base">Tableros NeuraSur (Power BI)</p>
                  <p className="text-xs text-emerald-700 font-semibold">Automatizado, cloud y en tiempo real</p>
                </div>
              </div>
              <ul className="space-y-3.5">
                {[
                  'Información actualizada automáticamente sin intervención humana manual.',
                  'Única fuente de la verdad para dueños, gerentes y líderes de equipo.',
                  'Filtros interactivos por fecha, sucursal, producto o vendedor en 1 clic.',
                  'Acceso seguro 24/7 desde cualquier PC, tablet o celular con alertas automáticas.',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="py-16 px-5 sm:px-8 bg-white border-t border-slate-200">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Preguntas Frecuentes</h2>
            <p className="text-sm text-slate-500 mt-2">Todo lo que necesitás saber antes de comenzar con tu primer tablero.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={i}
                  className={`rounded-xl overflow-hidden transition-all ${open ? 'border border-slate-300 shadow-sm bg-slate-50/50' : 'border border-slate-200 bg-white'}`}
                >
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-sm font-semibold text-slate-900">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-200/60 pt-3">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA Final ── */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50/50 border-t border-slate-200">
        <div className="max-w-[1200px] mx-auto">
          <div className="bg-slate-900 rounded-2xl px-8 sm:px-14 py-14 text-center relative overflow-hidden shadow-xl">
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }} />
            <div className="relative">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-950/80 border border-emerald-500/30 px-3 py-1 rounded-full mb-4 inline-block">
                Diagnóstico sin cargo
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight mt-2">
                Empezá a tomar decisiones<br className="hidden sm:block" /> con datos reales
              </h2>
              <p className="mt-4 text-slate-400 text-sm max-w-md mx-auto leading-relaxed">
                Coordinamos una videollamada de 30 minutos para revisar tus sistemas actuales y mostrarte cómo quedaría tu tablero.
              </p>
              <a
                href="https://wa.me/5491171299730?text=Hola!%20Quiero%20coordinar%20una%20demo%20de%20Power%20BI."
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-slate-950 font-bold px-8 py-3.5 rounded-lg text-sm transition-colors shadow-lg shadow-emerald-900/40"
              >
                <MessageSquare className="w-4 h-4" /> Hablar con un especialista
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 py-8 px-5 sm:px-8 bg-slate-50/50">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600 font-medium">
          <p>© 2026 NeuraSur. Todos los derechos reservados.</p>
          <p className="text-slate-500">
            Diseño e implementación por{' '}
            <a
              href="https://www.neurasur.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors underline-offset-4 hover:underline"
            >
              NeuraSur
            </a>
          </p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-emerald-600 transition-colors">Inicio</Link>
            <Link to="/blog" className="hover:text-emerald-600 transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PowerBIServicePage;
